'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  ChevronRight,
  CreditCard,
  Download,
  Flame,
  ImageIcon,
  Loader2,
  Sparkles,
  User,
  X,
  Info,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { Link } from '@/core/i18n/navigation';
import { AIMediaType, AITaskStatus } from '@/extensions/ai/types';
import {
  ImageUploader,
  ImageUploaderValue,
  LazyImage,
} from '@/shared/blocks/common';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { useAppContext } from '@/shared/contexts/app';
import {
  trackPhotoEffects,
  trackDailyCheckin,
  trackImageToImagePageView,
  trackEffectSelection,
  trackImageUpload,
  trackGenerateClick,
  trackCheckinDialogOpen,
} from '@/shared/lib/analytics';
import { cn } from '@/shared/lib/utils';

export interface ImageEditorPreset {
  id: string;
  name: string;
  emoji: string;
  description: string;
  tooltip: string;
  preview: string;
  prompt: string;
  isCustom?: boolean;
  // Translation keys
  nameKey?: string;
  descriptionKey?: string;
  tooltipKey?: string;
}

interface AIImageEditorProps {
  defaultPreset?: string;
  presets?: ImageEditorPreset[];
  allowMultipleImages?: boolean;
  maxImages?: number;
  maxSizeMB?: number;
  srOnlyTitle?: string;
}

interface GeneratedImage {
  id: string;
  url: string;
  preset?: string;
  prompt?: string;
}

interface BackendTask {
  id: string;
  status: string;
  provider: string;
  model: string;
  prompt: string | null;
  taskInfo: string | null;
  taskResult: string | null;
}

interface CheckinStatus {
  hasCheckedInToday: boolean;
  consecutiveDays: number;
  tomorrowRewardCredits: number;
}

interface ModelOption {
  value: string;
  label: string;
  provider: string;
  credits: number;
}

const POLL_INTERVAL = 5000;
const GENERATION_TIMEOUT = 180000;

// Model options
const MODEL_OPTIONS: ModelOption[] = [
  {
    value: 'google/nano-banana-pro',
    label: 'MAX',
    provider: 'replicate',
    credits: 5,
  },
  {
    value: 'bytedance/seedream-4',
    label: 'PRO',
    provider: 'replicate',
    credits: 1,
  },
];

// Default presets
const DEFAULT_PRESETS: ImageEditorPreset[] = [
  {
    id: 'enhance-portrait',
    name: 'Enhance Portrait',
    emoji: '🧑',
    description: 'Improve facial details, lighting, and skin texture without changing identity',
    tooltip: 'Best for selfies, profile photos, and portraits',
    preview: 'https://r2.zestygen.com/effects/10.webp',
    prompt: 'Enhance the portrait with natural skin tones, improved lighting, clearer facial details, and a professional, realistic look. Preserve the original face, expression, and identity.',
    nameKey: 'presets.enhance-portrait.name',
    descriptionKey: 'presets.enhance-portrait.description',
    tooltipKey: 'presets.enhance-portrait.tooltip',
  },
  {
    id: 'remove-object',
    name: 'Remove Object or Person',
    emoji: '✂️',
    description: 'Cleanly remove unwanted elements and restore the background',
    tooltip: 'Remove people, objects, or distractions from your image',
    preview: 'https://r2.zestygen.com/effects/70.jpg',
    prompt: 'Remove the selected object or person cleanly from the image and naturally reconstruct the background. Keep lighting, perspective, and surrounding details consistent.',
    nameKey: 'presets.remove-object.name',
    descriptionKey: 'presets.remove-object.description',
    tooltipKey: 'presets.remove-object.tooltip',
  },
  {
    id: 'change-background',
    name: 'Change Background',
    emoji: '🌄',
    description: 'Replace the background while keeping the subject intact',
    tooltip: 'Great for portraits, product photos, and social media',
    preview: 'https://r2.zestygen.com/effects/95.jpg',
    prompt: 'Replace the background with a clean, visually appealing new background while keeping the subject sharp and realistic. Match lighting, shadows, and depth naturally.',
    nameKey: 'presets.change-background.name',
    descriptionKey: 'presets.change-background.description',
    tooltipKey: 'presets.change-background.tooltip',
  },
  {
    id: 'product-enhancement',
    name: 'Product Photo Enhancement',
    emoji: '📦',
    description: 'Make product images clean, sharp, and professional',
    tooltip: 'Ideal for e-commerce, ads, and listings',
    preview: 'https://r2.zestygen.com/effects/20.webp',
    prompt: 'Enhance the product photo with clearer details, balanced lighting, accurate colors, and a clean professional look. Keep the product shape and texture unchanged.',
    nameKey: 'presets.product-enhancement.name',
    descriptionKey: 'presets.product-enhancement.description',
    tooltipKey: 'presets.product-enhancement.tooltip',
  },
  {
    id: 'style-transfer',
    name: 'Light Style Transfer',
    emoji: '🎨',
    description: 'Apply a subtle artistic style without distorting the image',
    tooltip: 'Adds style while preserving structure and realism',
    preview: 'https://r2.zestygen.com/effects/90.jpg',
    prompt: 'Clean up the image by removing noise, blur, and visual imperfections. Restore clarity and details while keeping the image natural and realistic.',
    nameKey: 'presets.style-transfer.name',
    descriptionKey: 'presets.style-transfer.description',
    tooltipKey: 'presets.style-transfer.tooltip',
  },
  {
    id: 'custom',
    name: 'Custom Edit',
    emoji: '⚙️',
    description: 'Write your own instructions. Full control over how the image is edited',
    tooltip: 'Best for advanced or specific editing needs',
    preview: 'https://r2.zestygen.com/effects/80.jpg',
    prompt: '',
    isCustom: true,
    nameKey: 'presets.custom.name',
    descriptionKey: 'presets.custom.description',
    tooltipKey: 'presets.custom.tooltip',
  },
];

function parseTaskResult(taskResult: string | null): any {
  if (!taskResult) {
    return null;
  }

  try {
    return JSON.parse(taskResult);
  } catch (error) {
    console.warn('Failed to parse taskResult:', error);
    return null;
  }
}

function extractImageUrls(result: any): string[] {
  if (!result) {
    return [];
  }

  const output = result.output ?? result.images ?? result.data;

  if (!output) {
    return [];
  }

  if (typeof output === 'string') {
    return [output];
  }

  if (Array.isArray(output)) {
    return output
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          const candidate =
            item.url ?? item.uri ?? item.image ?? item.src ?? item.imageUrl;
          return typeof candidate === 'string' ? [candidate] : [];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof output === 'object') {
    const candidate =
      output.url ?? output.uri ?? output.image ?? output.src ?? output.imageUrl;
    if (typeof candidate === 'string') {
      return [candidate];
    }
  }

  return [];
}

export function AIImageEditor({
  defaultPreset,
  presets = DEFAULT_PRESETS,
  allowMultipleImages = false,
  maxImages = 1,
  maxSizeMB = 24,
  srOnlyTitle,
}: AIImageEditorProps) {
  const t = useTranslations('photo-effects.common.editor');
  const tCheckin = useTranslations('common.checkin-button');

  const [selectedPreset, setSelectedPreset] = useState<string>(
    defaultPreset || presets[0]?.id || ''
  );
  const [selectedModel, setSelectedModel] = useState<string>(MODEL_OPTIONS[0].value);
  const [referenceImageItems, setReferenceImageItems] = useState<
    ImageUploaderValue[]
  >([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );
  const [taskStatus, setTaskStatus] = useState<AITaskStatus | null>(null);
  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [editingInstructions, setEditingInstructions] = useState<string>('');

  // Checkin states
  const [isCheckinOpen, setIsCheckinOpen] = useState(false);
  const [isCheckinLoading, setIsCheckinLoading] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState<CheckinStatus | null>(null);
  const [mounted, setMounted] = useState(false);

  const { user, isCheckSign, setIsShowSignModal, fetchUserCredits } =
    useAppContext();

  // Fix hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Track page view on component mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackImageToImagePageView({
        default_effect: defaultPreset,
        page_path: window.location.pathname,
      });
    }
  }, [defaultPreset]);

  // Reorder presets to put defaultPreset first if it exists
  const orderedPresets = useMemo(() => {
    if (!defaultPreset) return presets;

    const defaultIndex = presets.findIndex((p) => p.id === defaultPreset);
    if (defaultIndex === -1) return presets;

    const reordered = [...presets];
    const [defaultPresetItem] = reordered.splice(defaultIndex, 1);
    return [defaultPresetItem, ...reordered];
  }, [defaultPreset, presets]);

  const currentPreset = orderedPresets.find((p) => p.id === selectedPreset);
  const currentModel = MODEL_OPTIONS.find((m) => m.value === selectedModel);
  const costCredits = currentModel?.credits || 1;
  const remainingCredits = user?.credits?.remainingCredits ?? 0;

  // Update editing instructions when preset changes
  useEffect(() => {
    if (currentPreset && !currentPreset.isCustom) {
      setEditingInstructions(currentPreset.prompt);
    } else if (currentPreset?.isCustom) {
      setEditingInstructions('');
    }
  }, [currentPreset]);

  const handleGenerate = async () => {
    if (referenceImageUrls.length === 0) {
      toast.error(t('errors.no-image'));
      return;
    }

    if (!currentPreset) {
      toast.error(t('errors.no-preset'));
      return;
    }

    // Check if custom preset has prompt
    if (currentPreset.isCustom && !editingInstructions.trim()) {
      toast.error(t('errors.no-instructions'));
      return;
    }

    // Check if non-custom preset has editing instructions
    if (!currentPreset.isCustom && !editingInstructions.trim()) {
      toast.error(t('errors.no-instructions'));
      return;
    }

    if (isCheckSign) {
      setIsShowSignModal(true);
      return;
    }

    if (remainingCredits < costCredits) {
      toast.error(t('errors.insufficient-credits'));
      return;
    }

    // Track generate button click
    trackGenerateClick({
      effect_id: selectedPreset,
      effect_name: currentPreset.name,
      is_custom: currentPreset.isCustom || false,
      has_custom_prompt: !!editingInstructions.trim(),
      credits_cost: costCredits,
      remaining_credits: remainingCredits,
    });

    setIsGenerating(true);
    setProgress(0);
    setTaskStatus(null);
    setGenerationStartTime(Date.now());

    try {
      const options: any = {
        image_input: referenceImageUrls,
      };

      const response = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mediaType: AIMediaType.IMAGE,
          scene: 'photo-effects',
          provider: currentModel?.provider,
          model: selectedModel,
          prompt: editingInstructions,
          options,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Generation failed');
      }

      const { code, message, data } = await response.json();
      if (code !== 0) {
        throw new Error(message || 'Generation failed');
      }

      const newTaskId = data?.id;
      if (!newTaskId) {
        throw new Error('Task id missing in response');
      }

      // Check if task completed immediately
      if (data.status === AITaskStatus.SUCCESS && data.taskInfo) {
        const parsedResult = parseTaskResult(data.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (imageUrls.length > 0) {
          const timestamp = Date.now();
          const newImages: GeneratedImage[] = imageUrls.map((url, index) => ({
            id: `${newTaskId}-${timestamp}-${index}`,
            url,
            preset: selectedPreset,
            prompt: editingInstructions || undefined,
          }));

          setGeneratedImages((prev) => [...newImages, ...prev]);
          toast.success(t('success.edited'));

          trackPhotoEffects({
            effect: selectedPreset,
            credits_used: costCredits,
          });

          setIsGenerating(false);
          setProgress(100);
          setGenerationStartTime(null);
          setTaskStatus(null);
          await fetchUserCredits();
          return;
        }
      }

      setTaskId(newTaskId);
      await fetchUserCredits();
    } catch (error: any) {
      console.error('Image generation error:', error);
      toast.error(error.message || t('errors.generation-failed'));
      setIsGenerating(false);
      setProgress(0);
      setTaskStatus(null);
      setGenerationStartTime(null);
    }
  };

  useEffect(() => {
    if (!taskId || !isGenerating) {
      return;
    }

    let cancelled = false;

    const pollTask = async (): Promise<boolean> => {
      try {
        if (
          generationStartTime &&
          Date.now() - generationStartTime > GENERATION_TIMEOUT
        ) {
          toast.error(t('errors.generation-timeout'));
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        const response = await fetch('/api/ai/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ taskId }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const { code, message, data } = await response.json();
        if (code !== 0) {
          throw new Error(message || 'Query task failed');
        }

        const task = data as BackendTask;
        const currentStatus = task.status as AITaskStatus;
        setTaskStatus(currentStatus);

        const parsedResult = parseTaskResult(task.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (currentStatus === AITaskStatus.PENDING) {
          setProgress((prev) => Math.max(prev, 20));
          return false;
        }

        if (currentStatus === AITaskStatus.PROCESSING) {
          if (imageUrls.length > 0) {
            setProgress((prev) => Math.max(prev, 85));
          } else {
            setProgress((prev) => Math.min(prev + 10, 80));
          }
          return false;
        }

        if (currentStatus === AITaskStatus.SUCCESS) {
          if (imageUrls.length === 0) {
            toast.error('No image generated');
          } else {
            const timestamp = Date.now();
            const newImages: GeneratedImage[] = imageUrls.map((url, index) => ({
              id: `${taskId}-${timestamp}-${index}`,
              url,
              preset: selectedPreset,
              prompt: task.prompt || undefined,
            }));

            setGeneratedImages((prev) => [...newImages, ...prev]);
            toast.success(t('success.edited'));

            trackPhotoEffects({
              effect: selectedPreset,
              credits_used: costCredits,
            });
          }

          setProgress(100);
          setIsGenerating(false);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        if (currentStatus === AITaskStatus.FAILED) {
          const errorMessage = parsedResult?.errorMessage || t('errors.generation-failed');
          toast.error(errorMessage);
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          fetchUserCredits();
          return true;
        }

        setProgress((prev) => Math.min(prev + 5, 95));
        return false;
      } catch (error: any) {
        console.error('Error polling task status:', error);
        toast.error(`Query task failed: ${error.message}`);
        setIsGenerating(false);
        setProgress(0);
        setTaskId(null);
        setGenerationStartTime(null);
        setTaskStatus(null);
        fetchUserCredits();
        return true;
      }
    };

    pollTask();

    const interval = setInterval(async () => {
      if (cancelled || !taskId) {
        clearInterval(interval);
        return;
      }
      const completed = await pollTask();
      if (completed) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId, isGenerating, generationStartTime, selectedPreset, costCredits, fetchUserCredits]);

  const handleDownloadImage = async (image: GeneratedImage) => {
    if (!image.url) {
      return;
    }

    try {
      setDownloadingImageId(image.id);
      const response = await fetch(
        `/api/proxy/file?url=${encodeURIComponent(image.url)}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `zestygen-edited-${image.preset}-${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
      toast.success(t('success.downloaded'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('errors.download-failed'));
    } finally {
      setDownloadingImageId(null);
    }
  };

  const handleSelectPreset = (presetId: string) => {
    const preset = orderedPresets.find((p) => p.id === presetId);
    if (preset) {
      trackEffectSelection({
        effect_id: presetId,
        effect_name: preset.name,
        is_custom: preset.isCustom || false,
      });
    }
    setSelectedPreset(presetId);
  };

  // Handle opening checkin dialog
  const handleOpenCheckinDialog = async () => {
    trackCheckinDialogOpen({
      source: 'ai_image_editor',
    });

    if (!user) {
      setIsShowSignModal(true);
      return;
    }

    setIsCheckinOpen(true);

    try {
      const response = await fetch('/api/checkin/claim?action=status');
      if (response.ok) {
        const status = await response.json();
        setCheckinStatus(status);
      }
    } catch (error) {
      console.error('Failed to fetch checkin status:', error);
    }
  };

  const handleCheckin = async () => {
    if (!user) {
      setIsCheckinOpen(false);
      return;
    }

    setIsCheckinLoading(true);
    try {
      const response = await fetch('/api/checkin/claim', {
        method: 'POST',
      });

      if (!response.ok) {
        const error = await response.json();

        trackDailyCheckin({
          credits_earned: 0,
          consecutive_days: checkinStatus?.consecutiveDays || 0,
          success: false,
          error_message: error.error || 'Checkin failed',
        });

        throw new Error(error.error || 'Checkin failed');
      }

      const result = await response.json();
      const dayKey = Math.min(result.consecutiveDays, 5);

      trackDailyCheckin({
        credits_earned: result.rewardCredits,
        consecutive_days: result.consecutiveDays,
        success: true,
      });

      toast.success(`Day ${dayKey} Check-in Success!`, {
        description: `You earned ${result.rewardCredits} credits!`,
        duration: 3000,
      });

      setCheckinStatus({
        hasCheckedInToday: true,
        consecutiveDays: result.consecutiveDays,
        tomorrowRewardCredits: Math.min(result.consecutiveDays + 1, 5),
      });

      await fetchUserCredits();

      setTimeout(() => {
        setIsCheckinOpen(false);
      }, 2000);
    } catch (error: any) {
      toast.error('Checkin failed', {
        description: error.message,
      });
    } finally {
      setIsCheckinLoading(false);
    }
  };

  return (
    <section className="py-4 md:py-6">
      <div className="container">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left Column - Upload & Edit */}
            <Card>
              <CardHeader className="pb-4">
                {srOnlyTitle && <h2 className="sr-only">{srOnlyTitle}</h2>}
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  {t('upload.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pb-4">
                {/* Image Upload */}
                <div className="space-y-2">
                  <ImageUploader
                    defaultPreviews={referenceImageUrls}
                    onChange={(items) => {
                      setReferenceImageItems(items);
                      const urls = items
                        .filter((item) => item.status === 'uploaded' && item.url)
                        .map((item) => item.url!);
                      setReferenceImageUrls(urls);

                      const uploadedItems = items.filter((item) => item.status === 'uploaded');
                      if (uploadedItems.length > 0) {
                        const totalSizeMB = uploadedItems.reduce((sum, item) => {
                          return sum + (item.size || 0);
                        }, 0) / (1024 * 1024);

                        trackImageUpload({
                          image_count: uploadedItems.length,
                          total_size_mb: parseFloat(totalSizeMB.toFixed(2)),
                        });
                      }
                    }}
                    allowMultiple={allowMultipleImages}
                    maxImages={maxImages}
                    maxSizeMB={maxSizeMB}
                    emptyHint={t('upload.hint')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('upload.format')}
                  </p>
                </div>

                {/* Preset Selector */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-base font-semibold">
                      {t('presets.title')}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('presets.subtitle')}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {orderedPresets.map((preset) => {
                      const isSelected = selectedPreset === preset.id;
                      return (
                        <TooltipProvider key={preset.id}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleSelectPreset(preset.id)}
                                className={cn(
                                  'group relative overflow-hidden rounded-md transition-all duration-200',
                                  isSelected
                                    ? 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                                    : 'hover:ring-2 hover:ring-muted-foreground/30 hover:ring-offset-1 hover:ring-offset-background'
                                )}
                              >
                                <div className="relative w-full h-20 overflow-hidden rounded-t-md">
                                  <img
                                    src={preset.preview}
                                    alt={preset.name}
                                    className={cn(
                                      'h-full w-full object-cover transition-transform duration-300',
                                      isSelected ? 'scale-105' : 'group-hover:scale-105'
                                    )}
                                  />
                                  {isSelected && (
                                    <div className="absolute inset-0 bg-primary/20" />
                                  )}
                                  {isSelected && (
                                    <div className="absolute right-1 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
                                      <Check className="h-2 w-2" />
                                    </div>
                                  )}
                                </div>
                                <div
                                  className={cn(
                                    'flex items-center justify-center gap-0.5 px-1 py-1 text-center text-xs font-medium transition-colors',
                                    isSelected
                                      ? 'bg-primary text-primary-foreground'
                                      : 'bg-muted/50 group-hover:bg-muted'
                                  )}
                                >
                                  <span className="text-xs">{preset.emoji}</span>
                                  <span className="text-[9px] leading-tight truncate">{preset.name}</span>
                                </div>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-sm">{preset.tooltipKey ? t(preset.tooltipKey) : preset.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>

                  {/* Preset Description */}
                  {currentPreset && (
                    <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
                      <div className="flex items-start gap-2">
                        <span className="text-lg mt-0.5">{currentPreset.emoji}</span>
                        <div className="flex-1 space-y-1">
                          <h4 className="text-sm font-semibold text-foreground">
                            {currentPreset.nameKey ? t(currentPreset.nameKey) : currentPreset.name}
                          </h4>
                          <p className="text-xs text-foreground/80 leading-relaxed">
                            {currentPreset.descriptionKey ? t(currentPreset.descriptionKey) : currentPreset.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Editing Instructions / Prompt */}
                <div className="space-y-2">
                  <Label htmlFor="editing-instructions" className="text-sm font-medium">
                    {t('instructions.title')}
                  </Label>
                  <Textarea
                    id="editing-instructions"
                    placeholder={
                      currentPreset?.isCustom
                        ? t('instructions.placeholder-custom')
                        : t('instructions.placeholder-preset')
                    }
                    value={editingInstructions}
                    onChange={(e) => setEditingInstructions(e.target.value)}
                    className="min-h-[120px] resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('instructions.hint')}
                  </p>
                </div>

                {/* Model Selection */}
                <div className="space-y-2">
                  <Label>{t('model.label')}</Label>
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger className="h-auto w-full py-3.5">
                      <SelectValue>
                        {(() => {
                          const model = MODEL_OPTIONS.find(opt => opt.value === selectedModel);
                          return model ? (
                            <div className="flex flex-col items-start gap-1">
                              <span className="font-medium leading-none">{model.label}</span>
                              <span className="text-muted-foreground text-xs font-normal leading-tight">
                                {model.label === 'MAX'
                                  ? t('model.max-description')
                                  : t('model.pro-description')
                                }
                              </span>
                            </div>
                          ) : null;
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex flex-col items-start gap-0.5">
                            <span className="font-medium">{option.label}</span>
                            <span className="text-muted-foreground text-xs">
                              {option.label === 'MAX'
                                ? t('model.max-description')
                                : t('model.pro-description')
                              }
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Credits Info */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" />
                    <span>{t('credits.cost', { count: costCredits })}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    <span>{t('credits.remaining', { count: remainingCredits })}</span>
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    referenceImageUrls.length === 0 ||
                    !currentPreset ||
                    !editingInstructions.trim()
                  }
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('buttons.generating')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t('buttons.generate')}
                    </>
                  )}
                </Button>

                {isGenerating && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} />
                    {taskStatus && (
                      <p className="text-center text-xs text-muted-foreground">
                        Status: {taskStatus}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right Column - Results */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  <ImageIcon className="h-5 w-5" />
                  {generatedImages.length > 0 ? t('results.title-edited') : t('results.title-preview')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedImages.length === 0 ? (
                  <div className="space-y-4">
                    <div className="relative min-h-[500px] overflow-hidden rounded-lg border-2 border-dashed flex items-center justify-center">
                      {currentPreset && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-4 space-y-4">
                          <img
                            src={currentPreset.preview}
                            alt={currentPreset.nameKey ? t(currentPreset.nameKey) : currentPreset.name}
                            className="max-h-[400px] max-w-full object-contain rounded-lg"
                          />
                          <div className="text-center space-y-2 max-w-md">
                            <p className="text-sm font-medium text-foreground">
                              {t('results.example-label')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t('results.example-hint')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <ScrollArea className="h-[600px] pr-4">
                      <div className="space-y-4">
                        {generatedImages.map((image) => (
                          <div
                            key={image.id}
                            className="group relative overflow-hidden rounded-lg border"
                          >
                            <LazyImage
                              src={image.url}
                              alt={`Edited with ${image.preset}`}
                              className="w-full"
                            />
                            <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleDownloadImage(image)}
                                disabled={downloadingImageId === image.id}
                              >
                                {downloadingImageId === image.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Download className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    {/* Success Message */}
                    <div className="mt-6 rounded-lg border border-green-500/20 bg-gradient-to-r from-green-500/5 to-green-500/10 p-4">
                      <div className="text-center space-y-2">
                        <p className="text-sm font-medium text-foreground">
                          {t('results.success-title')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('results.success-description')}
                        </p>
                      </div>
                    </div>
                    {/* Checkin Prompt */}
                    <div className="mt-4 rounded-lg border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 p-4">
                      <div className="text-center space-y-2">
                        <p className="text-sm font-medium text-foreground">
                          {t('results.checkin-title')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('results.checkin-description')}
                        </p>
                        <Button
                          size="sm"
                          variant="default"
                          className="mt-2"
                          onClick={handleOpenCheckinDialog}
                        >
                          {t('results.checkin-button')}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Checkin Dialog - Reusing the same dialog from ImageToImageGenerator */}
      {mounted && (
        <Dialog open={isCheckinOpen} onOpenChange={setIsCheckinOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">
                <Flame className="inline h-5 w-5 text-orange-500 mr-2" />
                Daily Check-in
              </DialogTitle>
              <DialogDescription className="text-sm">
                Check in daily to earn free credits
              </DialogDescription>
            </DialogHeader>

            {checkinStatus ? (
              <div className="space-y-5">
                {!checkinStatus.hasCheckedInToday && (
                  <div className="rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-5 space-y-3">
                    <h3 className="text-base font-semibold text-foreground">
                      Today's Reward
                    </h3>
                    <div className="space-y-2">
                      <p className="text-lg font-bold text-primary">
                        +{checkinStatus.tomorrowRewardCredits} Credits
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Day {checkinStatus.consecutiveDays + 1} reward
                      </p>
                    </div>
                  </div>
                )}

                {checkinStatus.hasCheckedInToday && (
                  <div className="rounded-xl border-2 border-green-500/30 bg-gradient-to-br from-green-50 via-green-100/50 to-green-50 dark:from-green-950/20 dark:via-green-900/30 dark:to-green-950/20 p-5 space-y-3">
                    <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                      <span className="text-2xl">🎉</span>
                      Checked in successfully!
                    </h3>
                    <div className="space-y-2">
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">
                        Credits earned today
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Come back tomorrow for more
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-center gap-4 py-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {checkinStatus.consecutiveDays}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Consecutive days
                    </div>
                  </div>
                  {checkinStatus.hasCheckedInToday && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-500">✓</div>
                      <div className="text-xs text-muted-foreground">
                        Checked in
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleCheckin}
                  disabled={checkinStatus.hasCheckedInToday || isCheckinLoading}
                  size="lg"
                  className="w-full text-base font-semibold"
                >
                  {checkinStatus.hasCheckedInToday
                    ? 'Already Checked In'
                    : 'Check In Now'}
                </Button>

                <div className="text-center">
                  <Link
                    href="/settings/checkin"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    onClick={() => setIsCheckinOpen(false)}
                  >
                    View Details →
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-sm text-muted-foreground">
                  Loading...
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
